import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CreditCard, Plus, Upload, Loader2, FileCheck2, CheckCircle2, XCircle,
  Clock, Receipt, ExternalLink, Trash2, ShieldCheck, ArrowUpDown, X,
} from "lucide-react";

const METODOS = [
  { value: "sinpe", label: "SINPE Móvil" },
  { value: "transferencia", label: "Transferencia bancaria" },
  { value: "efectivo", label: "Efectivo" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "otro", label: "Otro" },
];
const MONEDAS = ["CRC", "USD"];
const ESTADOS = [
  { value: "pendiente", label: "Pendiente", cls: "bg-[#FBEED4] text-[#7A5312] border-[#E5C892]", Icon: Clock },
  { value: "aprobado", label: "Aprobado", cls: "bg-[#D8EFDD] text-[#155724] border-[#7BC48A]", Icon: CheckCircle2 },
  { value: "rechazado", label: "Rechazado", cls: "bg-[#FBEDEF] text-[#7A1E2E] border-[#E5A5B0]", Icon: XCircle },
];

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

const EMPTY_FORM = {
  monto: "", moneda: "CRC", metodo: "sinpe", referencia: "",
  fecha: "", periodo: "", notas: "",
  comprobante_base64: null, comprobante_filename: "", comprobante_mime: "",
};

function fmtMoney(n, moneda = "CRC") {
  const num = Math.round(Number(n) || 0);
  const prefix = moneda === "USD" ? "$ " : "₡ ";
  return prefix + new Intl.NumberFormat("es-CR").format(num);
}

function estadoBadge(estado) {
  const e = ESTADOS.find((x) => x.value === estado) || ESTADOS[0];
  const Icon = e.Icon;
  return (
    <Badge className={`${e.cls} font-semibold border inline-flex items-center gap-1`}>
      <Icon className="h-3 w-3" strokeWidth={2.2} /> {e.label}
    </Badge>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ============================================================================
export default function Pagos() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    aprobado: { CRC: 0, USD: 0 },
    pendiente: { CRC: 0, USD: 0 },
    rechazado: { CRC: 0, USD: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");
  const [regDialog, setRegDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rechazoTarget, setRechazoTarget] = useState(null);
  const [detalle, setDetalle] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/suscripcion/pagos"),
      api.get("/suscripcion/pagos/stats"),
    ])
      .then(([r1, r2]) => { setItems(r1.data); setStats(r2.data); })
      .catch(() => toast.error("Error cargando pagos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "todos") return items;
    return items.filter((p) => p.estado === filter);
  }, [items, filter]);

  const aprobar = async (p) => {
    try {
      await api.post(`/suscripcion/pagos/${p.id}/aprobar`);
      toast.success("Pago aprobado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo aprobar");
    }
  };

  const eliminar = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/suscripcion/pagos/${deleteTarget.id}`);
      toast.success("Pago eliminado");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-8" data-testid="module-suscripcion">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
            <CreditCard className="h-6 w-6 text-[#7A1E2E]" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#1A0810]">Pagos &amp; Suscripción</h1>
            <p className="mt-1 text-sm text-[#4a3838] max-w-2xl">
              Registrá tus pagos adjuntando el comprobante de SINPE o transferencia. El super administrador revisa y aprueba.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setRegDialog(true)}
          data-testid="btn-registrar-pago"
          className="bg-[#1F5B2E] hover:bg-[#144320] text-white h-11 px-5"
        >
          <Upload className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar pago
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total pagos" value={stats.total} icon={Receipt} tone="neutral" />
        <KpiCard
          label="Aprobado"
          value={fmtMoney(stats.aprobado?.CRC, "CRC")}
          sub={stats.aprobado?.USD ? fmtMoney(stats.aprobado.USD, "USD") : null}
          icon={CheckCircle2} tone="green"
        />
        <KpiCard
          label="Pendiente"
          value={fmtMoney(stats.pendiente?.CRC, "CRC")}
          sub={stats.pendiente?.USD ? fmtMoney(stats.pendiente.USD, "USD") : null}
          icon={Clock} tone="amber"
        />
        <KpiCard
          label="Rechazado"
          value={fmtMoney(stats.rechazado?.CRC, "CRC")}
          sub={stats.rechazado?.USD ? fmtMoney(stats.rechazado.USD, "USD") : null}
          icon={XCircle} tone="red"
        />
      </div>

      {/* Filtro por estado */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold mr-2">Historial</span>
        {[
          { v: "todos", l: "Todos" },
          { v: "pendiente", l: "Pendientes" },
          { v: "aprobado", l: "Aprobados" },
          { v: "rechazado", l: "Rechazados" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`filter-${f.v}`}
            className={`h-7 px-3 rounded-full text-[11px] font-semibold uppercase tracking-wide transition
              ${filter === f.v ? "bg-[#2B0913] text-[#F1EDE4]" : "bg-[#F5EEE0] text-[#4a3838] hover:bg-[#EEE4D2]"}`}
          >
            {f.l}
          </button>
        ))}
        {isSuper && (
          <span className="ml-3 inline-flex items-center gap-1 text-[11px] text-[#7A1E2E] font-semibold">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} /> Modo super administrador
          </span>
        )}
      </div>

      {/* Tabla histórica */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E] mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Receipt className="h-10 w-10 text-[#C19B6C] mx-auto" strokeWidth={1.3} />
            <p className="mt-4 text-sm text-[#6B5D57]">
              {items.length === 0 ? "Aún no has registrado ningún pago." : "Sin pagos en este filtro."}
            </p>
            {items.length === 0 && (
              <Button
                onClick={() => setRegDialog(true)}
                className="mt-6 bg-[#1F5B2E] hover:bg-[#144320] text-white"
              >
                <Upload className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar primer pago
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                  <TableHead className="text-[#3d2a2a] font-semibold">Fecha</TableHead>
                  {isSuper && <TableHead className="text-[#3d2a2a] font-semibold">Registrado por</TableHead>}
                  <TableHead className="text-[#3d2a2a] font-semibold">Método</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Referencia</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Período</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold text-right">Monto</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Comprobante</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const metodo = METODOS.find((m) => m.value === p.metodo)?.label || p.metodo;
                  const canDelete = isSuper || (p.created_by === user?.id && p.estado === "pendiente");
                  return (
                    <TableRow key={p.id} data-testid={`pago-row-${p.id}`} className="border-b border-[#E5DFD3] hover:bg-[#FBF9F4]">
                      <TableCell>
                        <div className="text-sm text-[#1A0810]">{p.fecha}</div>
                        <div className="text-[11px] text-[#6B5D57]">
                          Reg. {p.created_at?.slice(0, 10)}
                        </div>
                      </TableCell>
                      {isSuper && (
                        <TableCell>
                          <div className="text-sm text-[#1A0810]">{p.created_by_name || "—"}</div>
                          <div className="text-[11px] text-[#6B5D57]">{p.created_by_email}</div>
                        </TableCell>
                      )}
                      <TableCell><span className="text-sm text-[#3d2a2a]">{metodo}</span></TableCell>
                      <TableCell><span className="text-sm text-[#3d2a2a] font-mono">{p.referencia || "—"}</span></TableCell>
                      <TableCell><span className="text-sm text-[#3d2a2a]">{p.periodo || "—"}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="font-display text-sm text-[#1A0810]">{fmtMoney(p.monto, p.moneda)}</div>
                      </TableCell>
                      <TableCell>{estadoBadge(p.estado)}</TableCell>
                      <TableCell>
                        {p.comprobante_filename ? (
                          <button
                            onClick={() => setDetalle(p)}
                            data-testid={`ver-comprobante-${p.id}`}
                            className="inline-flex items-center gap-1 text-[12px] text-[#7A1E2E] hover:text-[#4A0E1C] font-semibold"
                          >
                            <FileCheck2 className="h-3.5 w-3.5" strokeWidth={1.8} /> Ver
                          </button>
                        ) : (
                          <span className="text-xs text-[#6B5D57]">Sin adjunto</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => setDetalle(p)}
                            data-testid={`pago-detalle-${p.id}`}
                            className="h-8 w-8 hover:bg-[#F5EEE0]" title="Ver detalle"
                          >
                            <ExternalLink className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                          </Button>
                          {isSuper && p.estado === "pendiente" && (
                            <>
                              <button
                                onClick={() => aprobar(p)}
                                data-testid={`pago-aprobar-${p.id}`}
                                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#16A34A] hover:bg-[#128036] text-white text-[11px] font-semibold uppercase tracking-wider"
                                title="Aprobar"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.2} /> Aprobar
                              </button>
                              <button
                                onClick={() => setRechazoTarget(p)}
                                data-testid={`pago-rechazar-${p.id}`}
                                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#7A1E2E] hover:bg-[#4A0E1C] text-white text-[11px] font-semibold uppercase tracking-wider"
                                title="Rechazar"
                              >
                                <XCircle className="h-3.5 w-3.5" strokeWidth={2.2} /> Rechazar
                              </button>
                            </>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => setDeleteTarget(p)}
                              data-testid={`pago-delete-${p.id}`}
                              className="h-8 w-8 hover:bg-[#FBEDEF]" title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <RegistrarPagoDialog
        open={regDialog}
        onOpenChange={setRegDialog}
        onSaved={() => { setRegDialog(false); load(); }}
      />

      <RechazoDialog
        target={rechazoTarget}
        onClose={() => setRechazoTarget(null)}
        onSaved={() => { setRechazoTarget(null); load(); }}
      />

      <DetalleDialog
        pago={detalle}
        isSuper={isSuper}
        onClose={() => setDetalle(null)}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar pago</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Deseas eliminar este registro? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={eliminar}
              data-testid="pago-confirm-delete"
              className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
function KpiCard({ label, value, sub, icon: Icon, tone }) {
  const tones = {
    neutral: "text-[#7A1E2E] bg-[#F5EEE0] border-[#C19B6C]/40",
    green: "text-[#1F5B2E] bg-[#EEF6EE] border-[#1F5B2E]/25",
    amber: "text-[#8A4A00] bg-[#FEE9CC] border-[#F3B872]",
    red: "text-[#7A1E2E] bg-[#FBEDEF] border-[#E5A5B0]",
  };
  return (
    <div className="rounded-xl bg-white border border-[#E5DFD3] p-5">
      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${tones[tone]}`}>
        <Icon className="h-3 w-3" strokeWidth={2} /> {label}
      </div>
      <div className="mt-3 font-display text-2xl text-[#1A0810]">{value}</div>
      {sub && <div className="text-[11px] text-[#6B5D57] mt-0.5">+ {sub}</div>}
    </div>
  );
}

// ============================================================================
function RegistrarPagoDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setForm({ ...EMPTY_FORM, fecha: today });
    }
  }, [open]);

  const onPickFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo supera 10 MB");
      return;
    }
    const dataUrl = await fileToBase64(file);
    setForm((s) => ({
      ...s,
      comprobante_base64: dataUrl,
      comprobante_filename: file.name,
      comprobante_mime: file.type,
    }));
  };

  const save = async () => {
    const monto = parseFloat(form.monto);
    if (isNaN(monto) || monto <= 0) { toast.error("Monto inválido"); return; }
    if (!form.fecha) { toast.error("La fecha es obligatoria"); return; }
    setSaving(true);
    try {
      await api.post("/suscripcion/pagos", { ...form, monto });
      toast.success("Pago registrado. Esperando aprobación.");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al registrar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="registrar-pago-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810]">Registrar pago</DialogTitle>
          <DialogDescription className="text-[#6B5D57]">
            Adjunta el comprobante para que el super administrador pueda aprobar tu pago.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className={labelCls}>Monto *</Label>
              <Input
                data-testid="pago-form-monto"
                type="number" min="0" step="0.01"
                value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
                className={`${inputCls} mt-2`} placeholder="50000"
              />
            </div>
            <div>
              <Label className={labelCls}>Moneda</Label>
              <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                <SelectTrigger data-testid="pago-form-moneda" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>{MONEDAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Método *</Label>
              <Select value={form.metodo} onValueChange={(v) => setForm({ ...form, metodo: v })}>
                <SelectTrigger data-testid="pago-form-metodo" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>{METODOS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Fecha del pago *</Label>
              <Input
                data-testid="pago-form-fecha"
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className={`${inputCls} mt-2`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Referencia / N° comprobante</Label>
              <Input
                data-testid="pago-form-referencia"
                value={form.referencia}
                onChange={(e) => setForm({ ...form, referencia: e.target.value })}
                className={`${inputCls} mt-2`}
                placeholder="SINPE 88881234 / Transf. 20260211-4487"
              />
            </div>
            <div>
              <Label className={labelCls}>Período que cubre</Label>
              <Input
                data-testid="pago-form-periodo"
                value={form.periodo}
                onChange={(e) => setForm({ ...form, periodo: e.target.value })}
                className={`${inputCls} mt-2`}
                placeholder="Feb 2026"
              />
            </div>
          </div>

          <div>
            <Label className={labelCls}>Comprobante (PDF o imagen · máx 10 MB)</Label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                className="hidden"
                data-testid="pago-form-file"
              />
              <Button
                type="button" variant="outline"
                onClick={() => fileRef.current?.click()}
                className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]"
              >
                <Upload className="h-4 w-4 mr-2" strokeWidth={1.8} />
                {form.comprobante_filename ? "Cambiar archivo" : "Subir archivo"}
              </Button>
              {form.comprobante_filename && (
                <div className="inline-flex items-center gap-2 text-sm text-[#1A0810]">
                  <FileCheck2 className="h-4 w-4 text-[#1F5B2E]" strokeWidth={1.8} />
                  <span className="truncate max-w-xs">{form.comprobante_filename}</span>
                  <button
                    onClick={() => setForm({ ...form, comprobante_base64: null, comprobante_filename: "", comprobante_mime: "" })}
                    className="h-6 w-6 rounded-md hover:bg-[#FBEDEF] flex items-center justify-center text-[#7A1E2E]"
                    title="Quitar"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className={labelCls}>Notas</Label>
            <Textarea
              data-testid="pago-form-notas"
              value={form.notas}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
              placeholder="Detalles adicionales para el super administrador"
              className="mt-2 min-h-[70px] bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
            />
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="pago-form-save"
            className="bg-[#1F5B2E] hover:bg-[#144320] text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar pago"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function RechazoDialog({ target, onClose, onSaved }) {
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (target) setMotivo(""); }, [target]);

  const rechazar = async () => {
    if (!motivo.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    setSaving(true);
    try {
      await api.post(`/suscripcion/pagos/${target.id}/rechazar`, { motivo: motivo.trim() });
      toast.success("Pago rechazado");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo rechazar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#FBF9F4] border-[#E5DFD3]" data-testid="rechazo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1A0810] flex items-center gap-2">
            <XCircle className="h-5 w-5 text-[#7A1E2E]" strokeWidth={2} /> Rechazar pago
          </DialogTitle>
          <DialogDescription className="text-[#6B5D57]">
            El usuario podrá ver el motivo del rechazo. Sé claro y respetuoso.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className={labelCls}>Motivo del rechazo *</Label>
          <Textarea
            data-testid="rechazo-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: El comprobante no coincide con el monto declarado"
            className="mt-2 min-h-[100px] bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
          <Button
            onClick={rechazar}
            disabled={saving}
            data-testid="rechazo-confirmar"
            className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rechazar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function DetalleDialog({ pago, isSuper, onClose }) {
  if (!pago) return null;
  const metodo = METODOS.find((m) => m.value === pago.metodo)?.label || pago.metodo;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const comprobanteUrl = pago.comprobante_filename
    ? `${backendUrl}/api/suscripcion/pagos/${pago.id}/comprobante`
    : null;

  return (
    <Dialog open={!!pago} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="pago-detalle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810] flex items-center gap-3">
            <Receipt className="h-6 w-6 text-[#7A1E2E]" strokeWidth={1.8} />
            {fmtMoney(pago.monto, pago.moneda)} · {metodo}
          </DialogTitle>
          <DialogDescription>
            <div className="mt-1">{estadoBadge(pago.estado)}</div>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <InfoField label="Fecha del pago" value={pago.fecha} />
          <InfoField label="Referencia" value={pago.referencia || "—"} />
          <InfoField label="Período" value={pago.periodo || "—"} />
          <InfoField label="Registrado" value={pago.created_at?.slice(0, 10)} />
          {isSuper && <InfoField label="Registrado por" value={`${pago.created_by_name} · ${pago.created_by_email}`} />}
          {pago.revisado_por_nombre && (
            <InfoField label="Revisado por" value={`${pago.revisado_por_nombre} · ${pago.revisado_at?.slice(0, 10)}`} />
          )}
        </div>

        {pago.notas && (
          <div>
            <div className={labelCls}>Notas</div>
            <div className="mt-2 rounded-md bg-white border border-[#E5DFD3] p-3 text-sm text-[#1A0810] whitespace-pre-wrap">
              {pago.notas}
            </div>
          </div>
        )}

        {pago.motivo_rechazo && (
          <div className="rounded-md bg-[#FBEDEF] border border-[#E5A5B0] p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A1E2E] font-semibold">Motivo del rechazo</div>
            <div className="mt-1 text-sm text-[#4A0E1C]">{pago.motivo_rechazo}</div>
          </div>
        )}

        {comprobanteUrl && (
          <div>
            <div className={labelCls}>Comprobante · {pago.comprobante_filename}</div>
            <div className="mt-2 rounded-md bg-white border border-[#E5DFD3] overflow-hidden">
              {pago.comprobante_mime?.startsWith("image/") ? (
                <img src={comprobanteUrl} alt="Comprobante" className="w-full max-h-[500px] object-contain bg-[#FBF9F4]" />
              ) : (
                <iframe
                  src={comprobanteUrl}
                  title="Comprobante"
                  className="w-full h-[500px]"
                />
              )}
            </div>
            <a
              href={comprobanteUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#7A1E2E] hover:text-[#4A0E1C] font-medium"
            >
              Abrir en nueva pestaña <ExternalLink className="h-3 w-3" strokeWidth={1.8} />
            </a>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="rounded-md bg-white border border-[#E5DFD3] p-3">
      <div className={labelCls}>{label}</div>
      <div className="mt-1 text-sm text-[#1A0810]">{value}</div>
    </div>
  );
}
